// cli/src/lib/risk.ts
// SPDX-License-Identifier: MIT
//
// Pure risk-code analyzer for proposal batch calls. Self-contained: also owns
// the calldata decoder and its inline ABI fragments (ERC-20, Moonwell cToken /
// comptroller, Uniswap SwapRouter single- and multi-hop) plus the verdict
// helper. No coupling to ./addresses.ts — callers inject KnownAddresses, so the
// module can be MIT-vendored as-is.
// Extracted from simulate.ts so it can be vendored by the public
// sherwood-guardian repo at https://github.com/sherwoodagent/sherwood-guardian.

import { decodeFunctionData, erc20Abi, type Abi, type Address, type Hex } from "viem";

// ── Types ────────────────────────────────────────────────

export interface BatchCall {
  target: Address;
  data: Hex;
  value: bigint;
}

export interface SimulationCallResult {
  index: number;
  target: Address;
  decodedFunction: string | null;
  selector: Hex;
  success: boolean;
  gasUsed: number;
}

export type RiskLevel = "critical" | "warning" | "info";

export interface RiskFlag {
  level: RiskLevel;
  code: string;
  message: string;
  callIndex?: number;
}

export interface KnownAddresses {
  [label: string]: Address | undefined;
}

export interface GovernorParamsLike {
  maxPerformanceFeeBps?: bigint;
}

export interface RiskContext {
  vault?: Address;
  knownAddresses?: KnownAddresses;
  performanceFeeBps?: bigint;
  strategyDuration?: bigint;
  governorParams?: GovernorParamsLike;
}

interface DecodedCall {
  functionName: string;
  args: readonly unknown[];
}

// ── ABI fragments (decoder coverage) ─────────────────────

const MOONWELL_CTOKEN_ABI = [
  { name: "mint", type: "function", stateMutability: "nonpayable", inputs: [{ name: "mintAmount", type: "uint256" }], outputs: [{ name: "", type: "uint256" }] },
  { name: "redeem", type: "function", stateMutability: "nonpayable", inputs: [{ name: "redeemTokens", type: "uint256" }], outputs: [{ name: "", type: "uint256" }] },
  { name: "redeemUnderlying", type: "function", stateMutability: "nonpayable", inputs: [{ name: "redeemAmount", type: "uint256" }], outputs: [{ name: "", type: "uint256" }] },
  { name: "borrow", type: "function", stateMutability: "nonpayable", inputs: [{ name: "borrowAmount", type: "uint256" }], outputs: [{ name: "", type: "uint256" }] },
  { name: "repayBorrow", type: "function", stateMutability: "nonpayable", inputs: [{ name: "repayAmount", type: "uint256" }], outputs: [{ name: "", type: "uint256" }] },
] as const;

const MOONWELL_COMPTROLLER_ABI = [
  { name: "enterMarkets", type: "function", stateMutability: "nonpayable", inputs: [{ name: "mTokens", type: "address[]" }], outputs: [{ name: "", type: "uint256[]" }] },
  { name: "exitMarket", type: "function", stateMutability: "nonpayable", inputs: [{ name: "mToken", type: "address" }], outputs: [{ name: "", type: "uint256" }] },
] as const;

const SWAP_ROUTER_SINGLE_ABI = [
  {
    name: "exactInputSingle", type: "function", stateMutability: "nonpayable",
    inputs: [{
      name: "params", type: "tuple",
      components: [
        { name: "tokenIn", type: "address" },
        { name: "tokenOut", type: "address" },
        { name: "fee", type: "uint24" },
        { name: "recipient", type: "address" },
        { name: "amountIn", type: "uint256" },
        { name: "amountOutMinimum", type: "uint256" },
        { name: "sqrtPriceLimitX96", type: "uint160" },
      ],
    }],
    outputs: [{ name: "amountOut", type: "uint256" }],
  },
] as const;

const SWAP_ROUTER_MULTI_ABI = [
  {
    name: "exactInput", type: "function", stateMutability: "nonpayable",
    inputs: [{
      name: "params", type: "tuple",
      components: [
        { name: "path", type: "bytes" },
        { name: "recipient", type: "address" },
        { name: "amountIn", type: "uint256" },
        { name: "amountOutMinimum", type: "uint256" },
      ],
    }],
    outputs: [{ name: "amountOut", type: "uint256" }],
  },
] as const;

// ── Address helpers ──────────────────────────────────────

export function buildLabelMapFromKnown(known: KnownAddresses): Map<string, string> {
  const map = new Map<string, string>();
  for (const [label, addr] of Object.entries(known)) {
    if (addr && addr !== "0x0000000000000000000000000000000000000000") {
      map.set(addr.toLowerCase(), label);
    }
  }
  return map;
}

export function truncAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function labeledAddr(addr: string, labels: Map<string, string>): string {
  const label = labels.get(addr.toLowerCase());
  return label ? `${label} (${truncAddr(addr)})` : truncAddr(addr);
}

// ── Calldata decoder ─────────────────────────────────────

function tryDecode(abi: Abi, data: Hex): DecodedCall | null {
  try {
    const decoded = decodeFunctionData({ abi, data });
    return { functionName: decoded.functionName, args: (decoded.args ?? []) as readonly unknown[] };
  } catch {
    return null;
  }
}

// Candidate ABIs are tried in order; the first selector match wins. Correctness
// depends on the 4-byte selectors being DISJOINT across every fragment below
// (ERC-20 transfer/transferFrom/approve, Moonwell cToken mint/redeem/borrow/...,
// comptroller enterMarkets/exitMarket, SwapRouter exactInputSingle, exactInput).
// They are today; adding a fragment to any pile without re-checking selector
// disjointness could silently mis-decode a call against the wrong ABI.
export function decodeCallArgs(data: Hex): DecodedCall | null {
  return (
    tryDecode(erc20Abi, data) ??
    tryDecode(MOONWELL_CTOKEN_ABI, data) ??
    tryDecode(MOONWELL_COMPTROLLER_ABI, data) ??
    tryDecode(SWAP_ROUTER_SINGLE_ABI, data) ??   // exactInputSingle (single-hop)
    tryDecode(SWAP_ROUTER_MULTI_ABI, data)       // exactInput (multi-hop) — restored, was dropped in extraction
  );
}

// ── Risk analyzer ────────────────────────────────────────

export function analyzeRisk(
  calls: BatchCall[],
  callResults: SimulationCallResult[],
  context?: RiskContext,
): RiskFlag[] {
  const risks: RiskFlag[] = [];
  const labels = buildLabelMapFromKnown(context?.knownAddresses ?? {});
  const vaultLower = context?.vault?.toLowerCase();

  const isKnown = (addr: string): boolean => {
    const lower = addr.toLowerCase();
    return labels.has(lower) || lower === vaultLower;
  };

  // A 0-call proposal (e.g. a performance-fee-only check) starts both false so it
  // doesn't emit vacuously-true ALL_TARGETS_VERIFIED / ALL_CALLS_DECODED info
  // flags. (Intentional change from the in-simulate.ts version, which started true.)
  let allTargetsVerified = calls.length > 0;
  let allCallsDecoded = calls.length > 0;

  for (let i = 0; i < calls.length; i++) {
    const call = calls[i];
    const result = callResults[i];
    const callNum = i + 1;

    if (result && !result.success) {
      risks.push({ level: "critical", code: "SIMULATION_FAILED",
        message: `Call #${callNum} to ${labeledAddr(call.target, labels)} reverted during simulation`, callIndex: i });
    }

    const targetKnown = isKnown(call.target);
    if (!targetKnown) {
      allTargetsVerified = false;
      risks.push({ level: "critical", code: "UNKNOWN_TARGET",
        message: `Call #${callNum} targets ${labeledAddr(call.target, labels)} which is not in the known address registry`, callIndex: i });
    }

    const decoded = decodeCallArgs(call.data);
    if (!decoded) {
      allCallsDecoded = false;
      if (!targetKnown) {
        risks.push({ level: "critical", code: "UNDECODED_CALLDATA",
          message: `Call #${callNum} has unrecognized calldata (selector ${call.data.slice(0, 10)}) targeting unknown contract`, callIndex: i });
      }
      continue;
    }

    if (decoded.functionName === "transfer" && decoded.args.length >= 2) {
      const to = decoded.args[0] as string;
      if (typeof to === "string" && to.startsWith("0x") && !isKnown(to)) {
        risks.push({ level: "critical", code: "TRANSFER_TO_UNKNOWN",
          message: `Call #${callNum} transfer() sends funds to ${truncAddr(to)} which is not a known protocol`, callIndex: i });
      }
    }
    if (decoded.functionName === "transferFrom" && decoded.args.length >= 3) {
      const to = decoded.args[1] as string;
      if (typeof to === "string" && to.startsWith("0x") && !isKnown(to)) {
        risks.push({ level: "critical", code: "TRANSFER_FROM_TO_UNKNOWN",
          message: `Call #${callNum} transferFrom() sends funds to ${truncAddr(to)} which is not a known protocol`, callIndex: i });
      }
    }
    if (decoded.functionName === "approve" && decoded.args.length >= 2) {
      const spender = decoded.args[0] as string;
      if (typeof spender === "string" && spender.startsWith("0x") && !isKnown(spender)) {
        risks.push({ level: "critical", code: "APPROVE_TO_UNKNOWN",
          message: `Call #${callNum} approve() grants allowance to ${truncAddr(spender)} which is not a known protocol`, callIndex: i });
      }
    }
  }

  if (context?.performanceFeeBps !== undefined) {
    const fee = context.performanceFeeBps;
    const maxFee = context.governorParams?.maxPerformanceFeeBps;
    if (maxFee !== undefined && fee >= (maxFee * 80n) / 100n) {
      risks.push({ level: "critical", code: "EXCESSIVE_PERFORMANCE_FEE",
        message: `Performance fee ${Number(fee) / 100}% is within 20% of the cap (${Number(maxFee) / 100}%)` });
    } else if (fee > 1000n) {
      risks.push({ level: "warning", code: "HIGH_PERFORMANCE_FEE",
        message: `Performance fee ${Number(fee) / 100}% exceeds the 10% guidance threshold` });
    }
  }

  if (context?.strategyDuration !== undefined) {
    const dur = context.strategyDuration;
    if (dur < 3600n) {
      risks.push({ level: "warning", code: "SHORT_STRATEGY_DURATION",
        message: `Strategy duration ${Number(dur)}s is under 1 hour — flash-loan-style risk` });
    }
    if (dur > 2_592_000n) {
      risks.push({ level: "warning", code: "LONG_STRATEGY_DURATION",
        message: `Strategy duration ${Number(dur) / 86400} days exceeds 30-day threshold` });
    }
  }

  const hasCritical = risks.some((r) => r.level === "critical");
  const hasWarning = risks.some((r) => r.level === "warning");
  if (!hasCritical && !hasWarning) {
    if (allTargetsVerified) {
      risks.push({ level: "info", code: "ALL_TARGETS_VERIFIED", message: "All call targets are verified protocol addresses" });
    }
    if (allCallsDecoded) {
      risks.push({ level: "info", code: "ALL_CALLS_DECODED", message: "All calldata successfully decoded" });
    }
  }

  return risks;
}

// ── Verdict helper ───────────────────────────────────────

export type Verdict = "CLEAN" | "WARNING" | "CRITICAL";

export function verdictOf(risks: RiskFlag[]): Verdict {
  if (risks.some((r) => r.level === "critical")) return "CRITICAL";
  if (risks.some((r) => r.level === "warning")) return "WARNING";
  return "CLEAN";
}
